import {useEffect, useState} from 'react';
import classNames from 'classnames';
import {interpolate} from 'utils/i18n';
import SlugAvailableQuery from '../../graphql/queries/SlugQuery';
import {useDebounce} from '../../hooks/useDebounce';
import fetchQuery from '../../graphql/fetchQuery';
import cookies from '../../utils/cookie';
import {useErrorLogger} from '@shopify/react-bugsnag';
import {LINKPOP_API_HOST} from '../../config/linkpop-api';
import {useI18n} from '../contexts/I18n.client';
import {RoutePath} from 'utils/links';

//TODO: Merge these with main repo
const MAX_SLUG_LENGTH = 255;
const SLUG_FROM_LANDING_PAGE = 'slugFromLandingPage';

export const SignupInput = ({onSignup}: {onSignup: () => void}) => {
  const logger = useErrorLogger();

  const [currentSlug, setCurrentSlug] = useState('');
  const [isDataLoading, setIsDataLoading] = useState(false);
  // TODO: Use a proper form library like react-form
  const [slugError, setSlugError] = useState<null | string>(null);
  const debouncedSlug = useDebounce(currentSlug, 500);

  const {t} = useI18n();

  useEffect(() => {
    if (!debouncedSlug || debouncedSlug.length === 0) {
      setIsDataLoading(false);
      setSlugError(null);
      return;
    }

    const verifySlug = async () => {
      try {
        // TODO: Setting error and loading state here is not clean architecture, but sadly as of now there are no graphql clients that support RSC. We need to build a generic graphql hook that handles loading, error, and data.

        const {data} = await fetchQuery({
          query: SlugAvailableQuery(debouncedSlug),
          onError: (error) => logger.notify(error),
        });

        if (data.slugAvailable) {
          setSlugError(null);
        } else {
          setSlugError(t('heroComponent.urlError.alreadyInUse'));
        }
      } catch (error) {
        setIsDataLoading(false);
        setSlugError(null);
        return null;
      } finally {
        setIsDataLoading(false);
      }
    };

    // TODO: Share validation code with main repo
    if (debouncedSlug.length > MAX_SLUG_LENGTH) {
      setSlugError(t('heroComponent.urlError.tooLong'));
    } else if (debouncedSlug.match(/[^a-zA-Z0-9-]/)) {
      setSlugError(t('heroComponent.urlError.specialCharacters'));
    } else {
      verifySlug();
    }
  }, [debouncedSlug]);

  const handleOnClick = async () => {
    if ('utag' in window) {
      await new Promise((res) =>
        window.utag.link(
          {
            event_category: 'link content',
            event_action: 'button-click',
            event_label: 'sign up for free',
          },
          res,
        ),
      );
    }

    cookies.set(SLUG_FROM_LANDING_PAGE, currentSlug, {
      path: '/',
      domain: LINKPOP_API_HOST,
    });
    onSignup();
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value;
    setIsDataLoading(true);
    setCurrentSlug(newSlug);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isDataLoading) {
      handleOnClick();
    }
  };

  return (
    <div>
      <div
        className={classNames(
          'flex',
          'items-center',
          'rounded-full',
          'border-[2px]',
          'border-black',
          'shadow-5px',
          'bg-white',
          'p-[0.188rem]',
          'pl-[0.938rem]',
          'md:pl-[1.125rem]',
          'text-base',

          // Larger font size at `lg` instead of `md`, otherwise it's too tight
          // at `md` sizes.
          'lg:text-xl',
        )}
      >
        <span>linkpop.com/</span>

        <input
          className={classNames('flex-1', 'pr-1', 'outline-none', 'min-w-0')}
          value={currentSlug}
          placeholder={t('heroComponent.inputPlaceholder')}
          onChange={handleOnChange}
          onKeyDown={handleKeyDown}
          data-testid="handle-input"
        />

        <button
          className={classNames(
            'whitespace-nowrap',
            'rounded-full',
            'bg-green-yellow',
            'disabled:bg-slate-200',
            'disabled:text-slate-400',
            'font-bold',
            'leading-none',
            'p-[0.938rem]',
            'text-[0.75rem]',
            'md:p-[1.125rem]',
            'md:text-[1rem]',
          )}
          onClick={handleOnClick}
          disabled={!!slugError}
          data-testid="sign-up-button-hero"
        >
          {t('heroComponent.buttonLabel')}
        </button>
      </div>

      <div
        className={classNames(
          'mt-3',
          'px-2',
          'h-8',
          'text-xs',
          'text-red-error',
          'flex',
        )}
      >
        {slugError && (
          <>
            <div
              className={classNames(
                'h-4',
                'w-4',
                'flex-shrink-0',
                'rounded-full',
                'mr-1',
                'bg-red-error',
                'text-white',
                'font-bold',
                'text-center',
              )}
            >
              !
            </div>

            <div>
              {interpolate(slugError, {
                signin: (content) => (
                  <a
                    href={RoutePath.Dashboard}
                    className="underline"
                    onClick={async (e) => {
                      if ('utag' in window) {
                        e.preventDefault();
                        const {href} = e.currentTarget;

                        await new Promise((res) =>
                          window.utag.link(
                            {
                              event_category: 'signup error message',
                              event_action: 'text-click',
                              event_label: 'login',
                            },
                            res,
                          ),
                        );

                        window.location.href = href;
                      }
                    }}
                  >
                    {content}
                  </a>
                ),
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
